import { snapdocsClosingApi } from 'app/services/api';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { SnapdocsClosingResource, SnapdocsClosingLoadState } from 'constants/snapdocsClosing';
import { catchAsyncActionError } from 'utils/action';
import { getDefaultErrorMessage } from 'utils/axios';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { getUUID } from 'utils/uuid';

export const SNAPDOCS_CLOSING_ACTION_TYPE = createNamedActions('snapdocsClosing', {
  GET_SNAPDOCS_CLOSING_REQUEST: 'GET_SNAPDOCS_CLOSING_REQUEST',
  GET_SNAPDOCS_CLOSING_SUCCESS: 'GET_SNAPDOCS_CLOSING_SUCCESS',
  GET_SNAPDOCS_CLOSING_FAILURE: 'GET_SNAPDOCS_CLOSING_FAILURE',
});

const blockReads = [
  {
    block: SnapdocsClosingResource.APPOINTMENT,
    read: params => snapdocsClosingApi.getAppointment(params),
  },
  {
    block: SnapdocsClosingResource.BORROWERS,
    read: params => snapdocsClosingApi.getBorrowers(params),
  },
  { block: SnapdocsClosingResource.NOTARY, read: params => snapdocsClosingApi.getNotary(params) },
  {
    block: SnapdocsClosingResource.QUALITY_CONTROL,
    read: params => snapdocsClosingApi.getQualityControl(params),
  },
  {
    block: SnapdocsClosingResource.COMMENTS,
    read: params => snapdocsClosingApi.getComments(params),
  },
];

const statusFailureLoadState = {
  [HTTP_RESPONSE_STATUS_CODES.NOT_FOUND]: SnapdocsClosingLoadState.NO_CLOSING,
  [HTTP_RESPONSE_STATUS_CODES.FORBIDDEN]: SnapdocsClosingLoadState.UNAVAILABLE,
};

const isNotFound = error => error?.response?.status === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND;

const readBlocks = async params => {
  const results = await Promise.allSettled(blockReads.map(({ read }) => read(params)));

  return results.reduce(
    (acc, result, index) => {
      const { block } = blockReads[index];

      if (result.status === 'fulfilled') {
        acc.blocks[block] = result.value.data;

        return acc;
      }

      if (!isNotFound(result.reason)) {
        acc.blockErrors[block] = getDefaultErrorMessage(result.reason);
      }

      return acc;
    },
    { blocks: {}, blockErrors: {} },
  );
};

export const getSnapdocsClosingAction = catchAsyncActionError(
  ({ applicationId, companyId }) =>
    async dispatch => {
      const params = { applicationId, companyId };
      const requestId = getUUID();

      dispatch({
        type: SNAPDOCS_CLOSING_ACTION_TYPE.GET_SNAPDOCS_CLOSING_REQUEST,
        payload: { applicationId, requestId },
      });

      try {
        const { data: status } = await snapdocsClosingApi.getStatus(params);
        const { blocks, blockErrors } = await readBlocks(params);

        dispatch({
          type: SNAPDOCS_CLOSING_ACTION_TYPE.GET_SNAPDOCS_CLOSING_SUCCESS,
          payload: { applicationId, requestId, status, blocks, blockErrors },
        });
      } catch (error) {
        dispatch({
          type: SNAPDOCS_CLOSING_ACTION_TYPE.GET_SNAPDOCS_CLOSING_FAILURE,
          payload: {
            applicationId,
            requestId,
            loadState:
              statusFailureLoadState[error?.response?.status] ?? SnapdocsClosingLoadState.ERROR,
            errorMessage: getDefaultErrorMessage(error),
          },
        });
      }
    },
);
